import React, { useEffect, useMemo, useState } from "react";
import { useLocation } from "react-router-dom";
import { Loader2, CheckCircle2, XCircle } from "lucide-react";
import { api } from "@/lib/api";
import { useT } from "@/lib/LanguageContext";
import ThemedPageLayout from "@/templates/ThemedPageLayout";
import LoadFailure from "../../components/ui/LoadFailure";
// Relative, not "@/…": lib/apiErrors.js is pure (no axios) and resolves for real under
// Jest, unlike "@/lib/api" (which this page's own test mocks only for `api.get`/`post`).
import { extractApiError } from "../../lib/apiErrors";

/**
 * `/desabonnement?t=<token>` — the marketing mail footer's "unsubscribe" link (contract
 * Public mail + webhook: `GET /api/public/email/unsubscribe?t` P, landing data, **no
 * mutation**; `POST … {t}` P, or the RFC 8058 one-click form a mail client's own
 * machinery posts directly — never through this page). Flips consent to `unsubscribed`
 * and halts every marketing enrolment for that client; transactional mail (session
 * reminders) never carries this link and is unaffected.
 *
 * GET NEVER MUTATES (link scanners): the read only confirms the token names a real
 * subscription, so a forged or expired link answers the SAME "invalid" panel as no
 * token at all — one uniform answer, never an oracle for which strings are tokens (the
 * doctrine `/avis` already applies to `REVIEW_TOKEN_UNKNOWN`). A 404 is that permanent
 * refusal; anything else (a dropped connection) is transient and gets a retry instead.
 * The WRITE only happens on the client's own click.
 *
 * Registered host-agnostic on both public route trees, same reasoning as `/gerer-rdv`,
 * `/avis` and `/rdv/confirmer`.
 */
export default function EmailUnsubscribe() {
  const t = useT("public");
  const tc = useT("common");
  const { search } = useLocation();
  const token = useMemo(() => new URLSearchParams(search).get("t") || "", [search]);

  const [loading, setLoading] = useState(!!token);
  const [invalid, setInvalid] = useState(!token);
  const [loadError, setLoadError] = useState(null);
  const [state, setState] = useState("idle"); // idle | busy | done | error
  const [actionError, setActionError] = useState("");

  const load = React.useCallback(async () => {
    setLoading(true);
    setLoadError(null);
    try {
      await api.get("/public/email/unsubscribe", { params: { t: token } });
      setInvalid(false);
    } catch (e) {
      if (e?.response?.status === 404) setInvalid(true);
      else setLoadError(e);
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => { if (token) load(); }, [token, load]);

  const unsubscribe = async () => {
    setState("busy");
    setActionError("");
    try {
      await api.post("/public/email/unsubscribe", { t: token });
      setState("done");
    } catch (e) {
      setActionError(extractApiError(e, tc("errors.saveFailed"), tc));
      setState("error");
    }
  };

  if (invalid) {
    return (
      <ThemedPageLayout>
        <section className="pt-32 pb-24 max-w-lg mx-auto px-6 text-center" data-testid="crm-email-unsubscribe-invalid">
          <XCircle size={40} className="mx-auto text-eh-muted" />
          <h1 className="font-display text-3xl text-eh-ink mt-4">{t("unsub.unknownTokenTitle")}</h1>
          <p className="text-eh-muted mt-3">{t("unsub.unknownTokenBody")}</p>
        </section>
      </ThemedPageLayout>
    );
  }

  if (loading) {
    return (
      <ThemedPageLayout>
        <div className="pt-40 pb-24 flex items-center justify-center text-eh-muted" data-testid="crm-email-unsubscribe-loading">
          <Loader2 className="animate-spin" size={20} />
        </div>
      </ThemedPageLayout>
    );
  }

  if (loadError) {
    return (
      <ThemedPageLayout>
        <section className="pt-32 pb-24 max-w-lg mx-auto px-6">
          <LoadFailure message={extractApiError(loadError, tc("errors.loadFailed"), tc)} onRetry={load}
            retryLabel={tc("actions.retry")} testId="crm-email-unsubscribe-error" />
        </section>
      </ThemedPageLayout>
    );
  }

  if (state === "done") {
    return (
      <ThemedPageLayout>
        <section className="pt-32 pb-24 max-w-lg mx-auto px-6 text-center" data-testid="crm-email-unsubscribe-done">
          <CheckCircle2 size={40} className="mx-auto text-eh-sage-deep" />
          <h1 className="font-display text-3xl text-eh-ink mt-4">{t("unsub.doneTitle")}</h1>
          <p className="text-eh-muted mt-3">{t("unsub.doneBody")}</p>
        </section>
      </ThemedPageLayout>
    );
  }

  return (
    <ThemedPageLayout>
      <section className="pt-32 pb-24 max-w-lg mx-auto px-6 text-center" data-testid="crm-email-unsubscribe">
        <span className="eyebrow">{t("unsub.eyebrow")}</span>
        <h1 className="font-display text-3xl md:text-4xl text-eh-ink mt-3">{t("unsub.title")}</h1>
        <p className="text-eh-muted mt-3">{t("unsub.body")}</p>
        <button
          type="button"
          onClick={unsubscribe}
          disabled={state === "busy"}
          data-testid="crm-email-unsubscribe-button"
          className="mt-8 inline-flex items-center gap-2 rounded-full bg-eh-ink text-eh-bg px-8 py-3 text-sm font-medium disabled:opacity-60"
        >
          {state === "busy" && <Loader2 className="animate-spin" size={16} aria-hidden="true" />}
          {t("unsub.button")}
        </button>
        {state === "error" && (
          <p role="alert" data-testid="crm-email-unsubscribe-error" className="mt-4 text-sm bg-red-50 border border-red-200 text-red-800 rounded-2xl p-4">
            {actionError}
          </p>
        )}
      </section>
    </ThemedPageLayout>
  );
}
